/** Ouvre une fenêtre imprimable pour enregistrer en PDF. */
export function openPrintablePdf(title: string, bodyHtml: string) {
  const w = window.open('', '_blank');
  if (!w) return;
  const safeTitle = escapeHtml(title);
  const generatedAt = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  w.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>${safeTitle}</title>
  <style>
    :root {
      --ink: #1a1a1a;
      --muted: #5a5a5a;
      --line: #c8c8c8;
      --soft: #f4f4f2;
      --accent: #1e4d3a;
    }
    * { box-sizing: border-box; }
    body {
      font-family: "Segoe UI", Calibri, "Helvetica Neue", Arial, sans-serif;
      padding: 1.75rem 2rem 2.5rem;
      color: var(--ink);
      line-height: 1.5;
      max-width: 980px;
      margin: 0 auto;
      font-size: 11pt;
    }
    .doc-header {
      border-bottom: 3px solid var(--accent);
      padding-bottom: 0.85rem;
      margin-bottom: 1.25rem;
    }
    .doc-brand {
      font-size: 0.75rem;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--accent);
      font-weight: 700;
      margin: 0 0 0.35rem;
    }
    h1 {
      font-size: 1.45rem;
      margin: 0;
      color: var(--ink);
      font-weight: 700;
      line-height: 1.25;
    }
    .doc-date {
      margin: 0.4rem 0 0;
      color: var(--muted);
      font-size: 0.85rem;
    }
    h2 {
      font-size: 1.05rem;
      margin: 1.4rem 0 0.55rem;
      color: var(--accent);
      border-bottom: 1px solid var(--line);
      padding-bottom: 0.25rem;
    }
    h3 {
      font-size: 0.98rem;
      margin: 1rem 0 0.35rem;
      color: var(--ink);
    }
    p { margin: 0.35rem 0 0.65rem; }
    table { border-collapse: collapse; width: 100%; font-size: 0.92rem; margin: 0.5rem 0 1rem; }
    th, td { border: 1px solid #888; padding: 0.45rem 0.55rem; vertical-align: top; text-align: left; }
    th { background: var(--soft); font-weight: 600; }
    ul { margin: 0.25rem 0 0.75rem; padding-left: 1.25rem; }
    li { margin: 0.2rem 0; }
    .meta { color: var(--muted); font-size: 0.9rem; margin-bottom: 1rem; }
    .badge {
      display: inline-block;
      padding: 0.2rem 0.55rem;
      border-radius: 4px;
      font-size: 0.85rem;
      font-weight: 600;
      background: var(--soft);
      border: 1px solid var(--line);
    }
    .badge.ok { background: #e8f5ef; border-color: #9ecbb5; color: #145c3a; }
    .badge.warn { background: #fdf2e9; border-color: #e0b48a; color: #8a4b12; }
    .field-block {
      margin: 0 0 0.85rem;
      padding: 0.65rem 0.75rem;
      background: var(--soft);
      border-left: 3px solid var(--accent);
    }
    .field-label {
      font-size: 0.78rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--muted);
      margin: 0 0 0.25rem;
      font-weight: 600;
    }
    .field-value {
      margin: 0;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .bmc-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1.2fr 1fr 1fr;
      grid-template-rows: auto auto auto;
      gap: 0.4rem;
      margin: 1rem 0;
    }
    .bmc-cell {
      border: 1px solid #777;
      padding: 0.5rem 0.55rem;
      min-height: 5.5rem;
      background: #fff;
    }
    .bmc-cell h4 {
      margin: 0 0 0.35rem;
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--accent);
      border-bottom: 1px solid var(--line);
      padding-bottom: 0.2rem;
    }
    .bmc-cell p {
      margin: 0;
      font-size: 0.82rem;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .bmc-kp { grid-column: 1; grid-row: 1 / 3; }
    .bmc-ka { grid-column: 2; grid-row: 1; }
    .bmc-kr { grid-column: 2; grid-row: 2; }
    .bmc-vp { grid-column: 3; grid-row: 1 / 3; background: #f0f7f3; }
    .bmc-cr { grid-column: 4; grid-row: 1; }
    .bmc-ch { grid-column: 4; grid-row: 2; }
    .bmc-cs { grid-column: 5; grid-row: 1 / 3; }
    .bmc-cost { grid-column: 1 / 3; grid-row: 3; }
    .bmc-rev { grid-column: 3 / 6; grid-row: 3; }
    .toolbar { margin-bottom: 1rem; }
    .toolbar button {
      font: inherit;
      padding: 0.55rem 1rem;
      background: var(--accent);
      color: #fff;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }
    .md-body h2 { font-size: 1.05rem; }
    .md-body h3 { font-size: 0.98rem; color: var(--accent); border: none; }
    .md-body p { white-space: pre-wrap; }
    @media print {
      .toolbar { display: none; }
      body { padding: 0; max-width: none; }
      .bmc-cell { break-inside: avoid; }
      .field-block { break-inside: avoid; }
    }
    @media (max-width: 720px) {
      .bmc-grid {
        display: block;
      }
      .bmc-cell { margin-bottom: 0.4rem; min-height: 0; }
    }
  </style></head><body>
  <div class="toolbar"><button type="button" onclick="window.print()">Imprimer / Enregistrer en PDF</button></div>
  <header class="doc-header">
    <p class="doc-brand">ISBD — Accompagnement entrepreneurial</p>
    <h1>${safeTitle}</h1>
    <p class="doc-date">Document généré le ${escapeHtml(generatedAt)}</p>
  </header>
  ${bodyHtml}
  </body></html>`);
  w.document.close();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function listHtml(items: string[]): string {
  if (!items.length) return '<p>—</p>';
  return `<ul>${items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`;
}

function metaTableHtml(meta: Record<string, string>): string {
  const rows = Object.entries(meta)
    .map(([k, v]) => `<tr><th>${escapeHtml(k)}</th><td>${escapeHtml(v || '—')}</td></tr>`)
    .join('');
  return `<table><tbody>${rows}</tbody></table>`;
}

function fieldBlockHtml(label: string, value: string): string {
  return `<div class="field-block"><p class="field-label">${escapeHtml(label)}</p><p class="field-value">${escapeHtml(value || '—')}</p></div>`;
}

/** Construit le HTML d’un rapport de diagnostic pour impression PDF. */
export function buildDiagnosticPdfHtml(opts: {
  meta: Record<string, string>;
  igpeScore: number;
  igpeLevel: string;
  totalObtenu: number;
  totalCible: number;
  dimensions: {
    label: string;
    note_cible: number;
    coefficient: number;
    total_cible: number;
    resultat: number;
    pct_cible: number;
  }[];
  results: string;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
}): string {
  const dimRows = opts.dimensions
    .map(
      (d) => `<tr>
      <td>${escapeHtml(d.label)}</td>
      <td>${d.note_cible}</td>
      <td>${d.coefficient}</td>
      <td>${d.total_cible}</td>
      <td><strong>${d.resultat}</strong></td>
      <td>${Math.round(d.pct_cible)} %</td>
    </tr>`,
    )
    .join('');

  return `
    <p class="meta">Score IGPE : <strong>${opts.igpeScore} %</strong> — ${escapeHtml(opts.igpeLevel)}
    (${opts.totalObtenu} / ${opts.totalCible})</p>
    <h2>Informations projet</h2>
    ${metaTableHtml(opts.meta)}
    <h2>Tableau des dimensions</h2>
    <table>
      <thead><tr>
        <th>Dimensions</th><th>Note cible</th><th>Coef.</th><th>Total cible</th><th>Résultats</th><th>% cible</th>
      </tr></thead>
      <tbody>${dimRows}</tbody>
      <tfoot><tr>
        <td>Total</td><td></td><td></td>
        <td>${opts.totalCible}</td><td>${opts.totalObtenu}</td><td>${opts.igpeScore} %</td>
      </tr></tfoot>
    </table>
    <h2>Conclusions</h2>
    <h3>1. Résultats</h3>
    <p>${escapeHtml(opts.results)}</p>
    <h3>2. Points forts</h3>
    ${listHtml(opts.strengths)}
    <h3>3. Points faibles</h3>
    ${listHtml(opts.weaknesses)}
    <h3>4. Recommandations</h3>
    ${listHtml(opts.recommendations)}
  `;
}

/** Business Model Canvas — grille classique Osterwalder. */
export function buildBmcPdfHtml(opts: {
  meta?: Record<string, string>;
  blocks: Record<string, string>;
}): string {
  const cell = (key: string, label: string, className: string) => `
    <div class="bmc-cell ${className}">
      <h4>${escapeHtml(label)}</h4>
      <p>${escapeHtml(opts.blocks[key] || '—')}</p>
    </div>`;

  return `
    ${opts.meta ? `<h2>Informations projet</h2>${metaTableHtml(opts.meta)}` : ''}
    <h2>Business Model Canvas</h2>
    <div class="bmc-grid">
      ${cell('key_partners', 'Partenaires clés', 'bmc-kp')}
      ${cell('key_activities', 'Activités clés', 'bmc-ka')}
      ${cell('key_resources', 'Ressources clés', 'bmc-kr')}
      ${cell('value_propositions', 'Proposition de valeur', 'bmc-vp')}
      ${cell('customer_relationships', 'Relations clients', 'bmc-cr')}
      ${cell('channels', 'Canaux', 'bmc-ch')}
      ${cell('customer_segments', 'Segments de clientèle', 'bmc-cs')}
      ${cell('cost_structure', 'Structure de coûts', 'bmc-cost')}
      ${cell('revenue_streams', 'Flux de revenus', 'bmc-rev')}
    </div>
  `;
}

/** Business Plan structuré par sections. */
export function buildBusinessPlanPdfHtml(opts: {
  meta?: Record<string, string>;
  sections: { title: string; fields: { label: string; value: string }[] }[];
}): string {
  const sectionsHtml = opts.sections
    .map((section) => {
      const fields = section.fields
        .map((f) => fieldBlockHtml(f.label, f.value))
        .join('');
      return `<h2>${escapeHtml(section.title)}</h2>${fields}`;
    })
    .join('');

  return `
    ${opts.meta ? `<h2>Informations projet</h2>${metaTableHtml(opts.meta)}` : ''}
    ${sectionsHtml}
  `;
}

/** Rapport de conclusions (faisabilité / diagnostic texte). */
export function buildConclusionReportPdfHtml(opts: {
  meta?: Record<string, string>;
  viability?: 'viable' | 'not_viable' | 'pending' | string;
  results: string;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
}): string {
  const viabilityLabel =
    opts.viability === 'viable'
      ? 'Viable'
      : opts.viability === 'not_viable'
        ? 'Non viable'
        : opts.viability === 'pending'
          ? 'En attente'
          : opts.viability || '—';
  const badgeClass =
    opts.viability === 'viable' ? 'ok' : opts.viability === 'not_viable' ? 'warn' : '';

  return `
    ${opts.meta ? `<h2>Informations projet</h2>${metaTableHtml(opts.meta)}` : ''}
    <p class="meta">Statut : <span class="badge ${badgeClass}">${escapeHtml(viabilityLabel)}</span></p>
    <h2>1. Résultats</h2>
    <p>${escapeHtml(opts.results || '—')}</p>
    <h2>2. Points forts</h2>
    ${listHtml(opts.strengths)}
    <h2>3. Points faibles</h2>
    ${listHtml(opts.weaknesses)}
    <h2>4. Recommandations</h2>
    ${listHtml(opts.recommendations)}
  `;
}

/**
 * Conversion Markdown légère (titres, listes, gras) → HTML pour synthèse stratégique.
 */
export function buildMarkdownPdfHtml(markdown: string): string {
  const lines = (markdown || '').replace(/\r\n/g, '\n').split('\n');
  const html: string[] = [];
  let inList = false;

  const flushList = () => {
    if (inList) {
      html.push('</ul>');
      inList = false;
    }
  };

  const inline = (text: string) =>
    escapeHtml(text)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>');

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^###\s+/.test(line)) {
      flushList();
      html.push(`<h3>${inline(line.replace(/^###\s+/, ''))}</h3>`);
      continue;
    }
    if (/^##\s+/.test(line)) {
      flushList();
      html.push(`<h2>${inline(line.replace(/^##\s+/, ''))}</h2>`);
      continue;
    }
    if (/^#\s+/.test(line)) {
      flushList();
      html.push(`<h2>${inline(line.replace(/^#\s+/, ''))}</h2>`);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      if (!inList) {
        html.push('<ul>');
        inList = true;
      }
      html.push(`<li>${inline(line.replace(/^[-*]\s+/, ''))}</li>`);
      continue;
    }
    if (!line.trim()) {
      flushList();
      continue;
    }
    flushList();
    html.push(`<p>${inline(line)}</p>`);
  }
  flushList();

  if (!html.length) {
    return '<p class="meta">Aucune synthèse générée.</p>';
  }
  return `<div class="md-body">${html.join('\n')}</div>`;
}
